/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {IxModalSize, ModalService} from '@siemens/ix-angular';
import {Subscription} from 'rxjs';
import {FeatureFlag} from '../../../models/FeatureFlag';
import {User} from '../../../models/User';
import {UserService} from '../../../services/user.service';
import {WindowSizeService} from '../../../services/window-size.service';
import {FeatureFlaggingService} from '../../../services/feature-flagging.service';
import {AppSettings} from '../../../models/Constants';

const MENU_DASHBOARD = 0;
const MENU_LIST_PASSPORTS = 1;
const MENU_LIST_REMOVED_PASSPORTS = 2;
const MENU_LIST_DATA_REQUESTS = 3;
const MENU_PMD = 4;
const MENU_USER_MANAGEMENT = 5;
const MENU_USER_ACCESS_REQUESTS = 6;

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  standalone: false,
})
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly featureFlaggingService = inject(FeatureFlaggingService);

  @ViewChild('menu', {read: ElementRef})
  menuRef!: ElementRef<HTMLIxMenuElement>;
  @ViewChild('feedbackModal', {read: TemplateRef})
  feedbackModalRef!: TemplateRef<HTMLElement>;

  email?: string;
  userData!: User;
  userName?: string;
  userAvatar?: string;

  loading = true;
  isCatenaXEnabled = false;

  menuItems = [true, false, false, false, false];

  private userDataSubscription?: Subscription;
  private routerSubscription?: Subscription;

  protected readonly sessionStorage = sessionStorage;

  featureFlag?: FeatureFlag;

  async ngOnInit() {
    this.userDataSubscription = this.userService.userDataChange.subscribe(
      this.setUserData.bind(this),
    );

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkMenuItems();
      }
    });

    this.checkMenuItems();
    this.featureFlag = await this.featureFlaggingService.load();
    if (this.featureFlag?.features.cx === true) {
      this.isCatenaXEnabled = true;
    }
  }

  checkMenuItems() {
    setTimeout(() => {
      const url = this.router.url.toLowerCase();
      const menuMap = [
        {pattern: 'dashboard', menu: MENU_DASHBOARD},
        {pattern: 'dpp/view-passport', menu: MENU_PMD},
        {pattern: 'dpp/list-passports', menu: MENU_PMD},
        {pattern: 'add-passport', menu: MENU_LIST_PASSPORTS},
        {pattern: 'view-passport', menu: MENU_LIST_PASSPORTS},
        {pattern: 'edit-passport', menu: MENU_LIST_PASSPORTS},
        {pattern: 'list-passports', menu: MENU_LIST_PASSPORTS},
        {pattern: 'list-removed-passports', menu: MENU_LIST_REMOVED_PASSPORTS},
        {pattern: 'assign-attributes', menu: MENU_LIST_DATA_REQUESTS},
        {pattern: 'list-user', menu: MENU_USER_MANAGEMENT},
        {pattern: 'invite-user', menu: MENU_USER_MANAGEMENT},
        {pattern: 'edit-user', menu: MENU_USER_MANAGEMENT},
        {pattern: 'access-requests', menu: MENU_USER_ACCESS_REQUESTS},
        {pattern: 'edit-access-request', menu: MENU_USER_ACCESS_REQUESTS},
      ];

      const found = menuMap.find((entry) => url.includes(entry.pattern));
      if (found) {
        this.setMenuItems(found.menu);
      } else {
        this.setMenuItems(MENU_DASHBOARD);
      }
    }, AppSettings.DEFAULT_VALUE_100);
  }

  async ngOnDestroy() {
    this.userDataSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  private setUserData(user: User | null) {
    if (user) {
      this.userData = user;
      this.email = user.email;
      this.userAvatar = user.picture;
      this.userName = user.name;
      this.loading = false;
    }
  }

  setMenuItems(id: number) {
    this.menuItems = Array.from({length: this.menuItems.length}).fill(
      false,
    ) as boolean[];
    this.menuItems[id] = true;
  }

  async onUserDataChange() {
    const {nativeElement} = this.menuRef;
    await nativeElement.toggleSettings(false);
  }

  async onLogoutClick() {
    await this.userService.logout();
  }

  async openFeedbackModal() {
    let size: IxModalSize;

    if (WindowSizeService.windowSizeSmall()) {
      size = '360';
    } else if (WindowSizeService.windowSizeLarge()) {
      size = '480';
    } else {
      size = '840';
    }

    await this.modalService.open({
      size,
      data: size,
      content: this.feedbackModalRef,
      centered: true,
      closeOnBackdropClick: true,
    });
  }

  getCopyRightYear() {
    return new Date().getFullYear();
  }
}
